export type TxnBenchmarkColumn = {
  key: string;
  value: string;
  minWidth?: number;
  locked?: boolean;
  align?: string;
};

type BenchmarkBlockKey =
  | "nifty50"
  | "bseSensex"
  | "niftyMidCap100"
  | "nifty500"
  | "niftyBank"
  | "niftyNext50";

const BENCHMARK_BLOCK_ALT_KEYS: Record<BenchmarkBlockKey, string[]> = {
  nifty50: ["Nifty50"],
  // Indi MF getRealisedReport uses `sensex` (not bseSensex).
  bseSensex: ["BSESensex", "sensex", "Sensex"],
  // Same API uses `midCap` for Nifty Midcap 100.
  niftyMidCap100: ["NiftyMidCap100", "midCap", "MidCap"],
  nifty500: ["Nifty500"],
  niftyBank: ["NiftyBank"],
  niftyNext50: ["NiftyNext50"],
};

/** Base Nifty50 / Sensex columns shown on holdings & txn reports. */
export const TXN_BENCHMARK_COLUMNS: TxnBenchmarkColumn[] = [
  { key: "nifty50GainLossPercentage", value: "Nifty 50 G/L\n(%)", minWidth: 84 },
  { key: "bseSensexGainLossPercentage", value: "BSE Sensex\n(%)", minWidth: 84 },
];

/** Extra index columns — optional Midcap / 500 / Bank / Next50 %. */
export const EQUITY_EXTRA_BENCHMARK_COLUMNS: TxnBenchmarkColumn[] = [
  { key: "nifty500GainLossPercentage", value: "Nifty 500 G/L\n(%)", minWidth: 92 },
  {
    key: "niftyMidCap100GainLossPercentage",
    value: "Nifty Midcap G/L\n(%)",
    minWidth: 100,
  },
  {
    key: "niftyNext50GainLossPercentage",
    value: "Nifty Next 50 G/L\n(%)",
    minWidth: 100,
  },
  { key: "niftyBankGainLossPercentage", value: "Nifty Bank G/L\n(%)", minWidth: 92 },
];

export const TXN_BENCHMARK_COLUMN_KEYS = TXN_BENCHMARK_COLUMNS.map((c) => c.key);

export const EQUITY_EXTRA_BENCHMARK_COLUMN_KEYS =
  EQUITY_EXTRA_BENCHMARK_COLUMNS.map((c) => c.key);

/** All known benchmark column keys (base + equity extras). */
export const ALL_TXN_BENCHMARK_COLUMN_KEYS = [
  ...TXN_BENCHMARK_COLUMN_KEYS,
  ...EQUITY_EXTRA_BENCHMARK_COLUMN_KEYS,
];

export const TXN_BENCHMARK_COLUMN_ORDER = [
  "nifty50GainLossPercentage",
  "bseSensexGainLossPercentage",
] as const;

export const EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER = [
  "nifty500GainLossPercentage",
  "niftyMidCap100GainLossPercentage",
  "niftyNext50GainLossPercentage",
  "niftyBankGainLossPercentage",
] as const;

const PERCENT_KEY_TO_BLOCK: Record<string, BenchmarkBlockKey> = {
  nifty50GainLossPercentage: "nifty50",
  bseSensexGainLossPercentage: "bseSensex",
  niftyMidCap100GainLossPercentage: "niftyMidCap100",
  nifty500GainLossPercentage: "nifty500",
  niftyBankGainLossPercentage: "niftyBank",
  niftyNext50GainLossPercentage: "niftyNext50",
};

const GAIN_KEY_TO_BLOCK: Record<string, BenchmarkBlockKey> = {
  nifty50GainLoss: "nifty50",
  bseSensexGainLoss: "bseSensex",
};

const toBenchmarkNum = (value: unknown): number | null => {
  if (value == null || value === "" || value === "-") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num;
};

const getBenchmarkTxnList = (row: Record<string, any>): any[] => {
  for (const key of [
    "transactions",
    "txns",
    "transactionDetails",
    "transactionList",
  ] as const) {
    const list = row?.[key];
    if (Array.isArray(list) && list.length > 0) return list;
  }
  return [];
};

const readBenchmarkBlock = (
  row: Record<string, any>,
  blockKey: BenchmarkBlockKey,
): Record<string, unknown> => {
  const altKeys = BENCHMARK_BLOCK_ALT_KEYS[blockKey];
  const meta = row?.metadata ?? row?.meta ?? {};

  const candidates = [blockKey, ...altKeys];
  for (const key of candidates) {
    const fromRoot = row?.[key];
    if (fromRoot && typeof fromRoot === "object") {
      return fromRoot as Record<string, unknown>;
    }
  }

  for (const key of candidates) {
    const fromMeta = meta?.[key];
    if (fromMeta && typeof fromMeta === "object") {
      return fromMeta as Record<string, unknown>;
    }
  }

  for (const txn of getBenchmarkTxnList(row)) {
    for (const key of candidates) {
      const fromTxn = txn?.[key];
      if (fromTxn && typeof fromTxn === "object") {
        return fromTxn as Record<string, unknown>;
      }
    }
  }

  return {};
};

const readBlockPercentWithCagrFallback = (
  block: Record<string, unknown>,
  flatFallbacks: unknown[],
): number | null => {
  // Previous nifty50 / sensex behavior: gainLossPercentage ?? cagr ?? flats
  const fromBlock =
    toBenchmarkNum(block.gainLossPercentage) ?? toBenchmarkNum(block.cagr);
  if (fromBlock != null) return fromBlock;
  for (const flat of flatFallbacks) {
    const n = toBenchmarkNum(flat);
    if (n != null) return n;
  }
  return null;
};

const readBlockGainLossPercentageOnly = (
  block: Record<string, unknown>,
  flatFallbacks: unknown[],
): number | null => {
  // Equity extra indexes: only gainLossPercentage (0 stays 0; missing → null → "-")
  const fromBlock = toBenchmarkNum(block.gainLossPercentage);
  if (fromBlock != null) return fromBlock;
  for (const flat of flatFallbacks) {
    const n = toBenchmarkNum(flat);
    if (n != null) return n;
  }
  return null;
};

const ALL_BENCHMARK_BLOCK_KEYS: string[] = [
  "nifty50",
  "Nifty50",
  "bseSensex",
  "BSESensex",
  "sensex",
  "Sensex",
  "niftyMidCap100",
  "NiftyMidCap100",
  "midCap",
  "MidCap",
  "nifty500",
  "Nifty500",
  "niftyBank",
  "NiftyBank",
  "niftyNext50",
  "NiftyNext50",
];

const FLAT_BENCHMARK_FALLBACK_KEYS = [
  "nifty50GainLoss",
  "nifty50GainLossPercentage",
  "niftyCagr",
  "niftyCAGR",
  "bseSensexGainLoss",
  "bseSensexGainLossPercentage",
  "sensexCagr",
  "sensexCAGR",
  "niftyMidCap100GainLossPercentage",
  "nifty500GainLossPercentage",
  "niftyBankGainLossPercentage",
  "niftyNext50GainLossPercentage",
] as const;

/**
 * Equity/REIT holdings APIs put Nifty/Sensex on the holding, not nested sells.
 * Copy those blocks onto flattened Realized / Transaction rows.
 */
export const pickHoldingBenchmarkFields = (
  holding: Record<string, any> | null | undefined,
): Record<string, unknown> => {
  if (!holding || typeof holding !== "object") return {};

  const picked: Record<string, unknown> = {};
  for (const key of ALL_BENCHMARK_BLOCK_KEYS) {
    const val = holding[key];
    if (val && typeof val === "object" && !Array.isArray(val)) {
      picked[key] = val;
    }
  }
  for (const key of FLAT_BENCHMARK_FALLBACK_KEYS) {
    if (holding[key] != null && holding[key] !== "") {
      picked[key] = holding[key];
    }
  }
  return picked;
};

export const extractTxnBenchmarkFields = (
  row: Record<string, any>,
): Record<string, number | null> => {
  const nifty50 = readBenchmarkBlock(row, "nifty50");
  const bseSensex = readBenchmarkBlock(row, "bseSensex");
  const niftyMidCap100 = readBenchmarkBlock(row, "niftyMidCap100");
  const nifty500 = readBenchmarkBlock(row, "nifty500");
  const niftyBank = readBenchmarkBlock(row, "niftyBank");
  const niftyNext50 = readBenchmarkBlock(row, "niftyNext50");

  return {
    nifty50GainLoss:
      toBenchmarkNum(nifty50.gainLoss) ?? toBenchmarkNum(row.nifty50GainLoss),
    // Nested G/L % (non-indi holdings path); flat niftyCAGR (indi realised API).
    nifty50GainLossPercentage: readBlockPercentWithCagrFallback(nifty50, [
      row.nifty50GainLossPercentage,
      row.niftyCAGR,
      row.niftyCagr,
    ]),
    bseSensexGainLoss:
      toBenchmarkNum(bseSensex.gainLoss) ??
      toBenchmarkNum(row.bseSensexGainLoss),
    bseSensexGainLossPercentage: readBlockPercentWithCagrFallback(bseSensex, [
      row.bseSensexGainLossPercentage,
      row.sensexCAGR,
      row.sensexCagr,
    ]),
    niftyMidCap100GainLossPercentage: readBlockGainLossPercentageOnly(
      niftyMidCap100,
      [row.niftyMidCap100GainLossPercentage],
    ),
    nifty500GainLossPercentage: readBlockGainLossPercentageOnly(nifty500, [
      row.nifty500GainLossPercentage,
    ]),
    niftyBankGainLossPercentage: readBlockGainLossPercentageOnly(niftyBank, [
      row.niftyBankGainLossPercentage,
    ]),
    niftyNext50GainLossPercentage: readBlockGainLossPercentageOnly(
      niftyNext50,
      [row.niftyNext50GainLossPercentage],
    ),
  };
};

/** Header / totalHoldingInfo — map only nested nifty50 & bseSensex objects. */
export const extractHeaderBenchmarkFields = (
  info: Record<string, any> | null | undefined,
): Record<string, number | null> => {
  const empty = {
    nifty50GainLoss: null,
    nifty50GainLossPercentage: null,
    bseSensexGainLoss: null,
    bseSensexGainLossPercentage: null,
  };
  if (!info || typeof info !== "object") return empty;

  const nifty50 = info.nifty50 ?? info.Nifty50 ?? {};
  const bseSensex = info.bseSensex ?? info.BSESensex ?? {};

  return {
    nifty50GainLoss: toBenchmarkNum(nifty50.gainLoss),
    nifty50GainLossPercentage: toBenchmarkNum(nifty50.gainLossPercentage),
    bseSensexGainLoss: toBenchmarkNum(bseSensex.gainLoss),
    bseSensexGainLossPercentage: toBenchmarkNum(bseSensex.gainLossPercentage),
  };
};

export const withTxnBenchmarkFields = <T extends Record<string, any>>(
  row: T,
): T & ReturnType<typeof extractTxnBenchmarkFields> => ({
  ...row,
  ...extractTxnBenchmarkFields(row),
});

/** Insert benchmark columns as 3rd–2nd from last (before the final column). */
export const withTxnBenchmarkColumns = <T extends TxnBenchmarkColumn>(
  columns: T[],
): T[] => {
  if (columns.length === 0) return columns;
  const last = columns[columns.length - 1];
  const rest = columns.slice(0, -1);
  return [...rest, ...(TXN_BENCHMARK_COLUMNS as T[]), last];
};

/** Append base Nifty50 / Sensex benchmark columns. */
export const appendTxnBenchmarkColumns = <T extends TxnBenchmarkColumn>(
  columns: T[],
): T[] => [...columns, ...(TXN_BENCHMARK_COLUMNS as T[])];

/** Append Equity Unrealized extra index % columns. */
export const appendEquityExtraBenchmarkColumns = <T extends TxnBenchmarkColumn>(
  columns: T[],
): T[] => [...columns, ...(EQUITY_EXTRA_BENCHMARK_COLUMNS as T[])];

const ALL_TXN_BENCHMARK_COLUMN_KEY_SET = new Set<string>(
  ALL_TXN_BENCHMARK_COLUMN_KEYS,
);

/** Ordered defs for any benchmarks present on a column set. */
const ALL_BENCHMARK_COLUMN_DEFS: TxnBenchmarkColumn[] = [
  ...TXN_BENCHMARK_COLUMNS,
  ...EQUITY_EXTRA_BENCHMARK_COLUMNS,
];

/** Core columns only — strips benchmark + member columns for reordering. */
export const stripTxnBenchmarkAndMemberColumns = <T extends { key: string }>(
  columns: T[],
): T[] =>
  columns.filter(
    (c) =>
      c.key !== "memberName" && !ALL_TXN_BENCHMARK_COLUMN_KEY_SET.has(c.key),
  );

/**
 * Benchmark cols are last when member is hidden.
 * When member is shown: benchmarks are before member (member last).
 * Preserves whichever benchmark columns are already present (base and/or equity extras).
 */
export const orderTxnBenchmarkAndMemberColumns = <T extends TxnBenchmarkColumn>(
  columns: T[],
  options?: { includeMemberName?: boolean; memberColumn?: T },
): T[] => {
  if (columns.length === 0) return columns;

  const includeMemberName = options?.includeMemberName === true;
  const hasBenchmarks = columns.some((c) =>
    ALL_TXN_BENCHMARK_COLUMN_KEY_SET.has(c.key),
  );
  const core = stripTxnBenchmarkAndMemberColumns(columns);

  if (!hasBenchmarks) {
    if (!includeMemberName) return core as T[];
    const memberCol =
      options?.memberColumn ??
      (columns.find((c) => c.key === "memberName") as T | undefined);
    if (memberCol) return [...(core as T[]), memberCol];
    return core as T[];
  }

  const benchmarkCols = ALL_BENCHMARK_COLUMN_DEFS.filter((bc) =>
    columns.some((c) => c.key === bc.key),
  ).map((bc) => {
    const existing = columns.find((c) => c.key === bc.key);
    return (existing ?? bc) as T;
  });

  if (!includeMemberName) {
    return [...(core as T[]), ...benchmarkCols];
  }

  const memberCol =
    options?.memberColumn ??
    (columns.find((c) => c.key === "memberName") as T | undefined);
  if (!memberCol) {
    return [...(core as T[]), ...benchmarkCols];
  }

  return [...(core as T[]), ...benchmarkCols, memberCol];
};

export const formatTxnBenchmarkAmount = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatTxnBenchmarkPercent = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `${num.toFixed(2)}%`;
};

export const TXN_BENCHMARK_HOLDING_SUMMARY_FIELDS = {
  nifty50GainLossPercentage: {
    label: "Nifty 50 G/L (%)",
    accessor: (tx: Record<string, any>) =>
      tx?.nifty50?.gainLossPercentage ??
      tx?.nifty50?.cagr ??
      tx?.nifty50GainLossPercentage ??
      tx?.niftyCAGR ??
      tx?.niftyCagr,
    format: formatTxnBenchmarkPercent,
  },
  bseSensexGainLossPercentage: {
    label: "BSE Sensex (%)",
    accessor: (tx: Record<string, any>) =>
      tx?.bseSensex?.gainLossPercentage ??
      tx?.bseSensex?.cagr ??
      tx?.bseSensexGainLossPercentage ??
      tx?.sensexCAGR ??
      tx?.sensexCagr,
    format: formatTxnBenchmarkPercent,
  },
};

export const insertTxnBenchmarkHoldingFields = (
  fields: Record<string, unknown>,
): Record<string, unknown> => {
  const keys = Object.keys(fields);
  if (keys.length === 0) return fields;
  const lastKey = keys[keys.length - 1];
  const ordered: Record<string, unknown> = {};
  for (const key of keys.slice(0, -1)) {
    ordered[key] = fields[key];
  }
  Object.assign(ordered, TXN_BENCHMARK_HOLDING_SUMMARY_FIELDS);
  ordered[lastKey] = fields[lastKey];
  return ordered;
};

const EQUITY_EXTRA_PERCENT_COLUMN_KEYS = new Set<string>(
  EQUITY_EXTRA_BENCHMARK_COLUMN_KEYS,
);

/** Resolve nested benchmark % for MainTable cell rendering. */
export const resolveTxnBenchmarkPercentValue = (
  data: Record<string, any> | null | undefined,
  columnKey: string,
): unknown => {
  if (!data) return undefined;
  const blockKey = PERCENT_KEY_TO_BLOCK[columnKey];
  if (!blockKey) return data?.[columnKey];

  const block = readBenchmarkBlock(data, blockKey);

  // New equity indexes: gainLossPercentage only (no cagr fallback).
  if (EQUITY_EXTRA_PERCENT_COLUMN_KEYS.has(columnKey)) {
    return block.gainLossPercentage ?? data?.[columnKey];
  }

  // Nifty50 / Sensex — previous behavior unchanged.
  return (
    block.gainLossPercentage ??
    block.cagr ??
    data?.[columnKey] ??
    (blockKey === "nifty50"
      ? (data?.niftyCAGR ?? data?.niftyCagr)
      : blockKey === "bseSensex"
        ? (data?.sensexCAGR ?? data?.sensexCagr)
        : undefined)
  );
};

export const resolveTxnBenchmarkGainValue = (
  data: Record<string, any> | null | undefined,
  columnKey: string,
): unknown => {
  if (!data) return undefined;
  const blockKey = GAIN_KEY_TO_BLOCK[columnKey];
  if (!blockKey) return data?.[columnKey];
  const block = readBenchmarkBlock(data, blockKey);
  return block.gainLoss ?? data?.[columnKey];
};

export const isTxnBenchmarkPercentColumn = (key: string): boolean =>
  key in PERCENT_KEY_TO_BLOCK;

export const isTxnBenchmarkGainColumn = (key: string): boolean =>
  key in GAIN_KEY_TO_BLOCK;
